import authService from './authService';
import SecureCookieManager from '../utils/secureCookie';
import { getToken } from 'src/utils/authUtils';
import { getV2Token } from '../utils/v2AuthUtils';
import { toast } from 'react-toastify';

let BASE_URL = process.env.REACT_APP_API_URL

async function loadConfig() {
    try {
        const response = await fetch("/config.json");
        const config = await response.json();
        BASE_URL = config.API_URL || BASE_URL; 
        //console.log("API URL Loaded:", BASE_URL);
    } catch (error) {
        console.error("Failed to load config.json. Using default .env API URL", error);
    }
}

loadConfig().then(() => {
    //console.log("Config initialized");
});

// Get CSRF token for protected endpoints
async function getCsrfToken(): Promise<string> {
  const response = await fetch(`${BASE_URL}/csrf/token`, {
    method: "GET",
    credentials: "include",
  });
  const data = await response.json();
  return data.csrfToken;
}



export async function loginUser(username: string, password: string,rememberMe: boolean) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/auth/loginDemo`, {
      method: 'POST',
      headers: {
          "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password ,rememberMe}),
    });

    if (!response.ok) {
        const errorData = await response.json();
        return { isSuccess: false, message: errorData.message || 'Login failed', failedLoginAttempts: errorData.failedLoginAttempts || 0 };
    }

    const data = await response.json();
   
    if(data.isSuccess){
        SecureCookieManager.setAuthToken(data.data.token.accessToken, rememberMe);
    }
    
    return data;
  } catch (error: any) {
      throw new Error(error.message || 'Login failed');
  }
}

export async function validateToken(token: string) {
  const response = await authService.apiCall(`${BASE_URL}/auth/validate-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    throw new Error('Failed to validate token');
  }

  const data = await response.json();
  if(data.isSuccess){
    SecureCookieManager.setAuthToken(data.data.token.accessToken);
  }
  return data;
}

export async function getUsers(query: string, perPage: number, pageNo: number): Promise<{ success: boolean; data?: any[], totalCount?: number, message?: string }> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/users/regtech?query=${query}&perPage=${perPage}&pageNo=${pageNo}`, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { success: false, message: errorData.message || 'Failed to get users' };
    }

    const data = await response.json();
    return data;

  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to get users' };
  }
}

export async function getUserRolePermissions(userId:string) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/groups/getGroupUsers/${userId}`, {
      method: 'GET'
    });
    
    const data = await response.json();
    return data;
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to get user role permissions' };
  }
}

export async function updateUserRole(id: string, data: Record<string, any>) {
  try {
    // Ensure the data is an object
    if (typeof data !== 'object' || data === null) {
      throw new Error('Invalid data provided to updateUserRole. Expected a JSON object.');
    }

    const response = await authService.apiCall(`${BASE_URL}/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      // Include detailed error response for debugging
      const errorText = await response.text();
      throw new Error(`Failed to update user role: ${response.status} - ${errorText}`);
    }

    const result = await response.json(); // Parse JSON safely
    return result;
  } catch (error: any) {
    console.error('Error updating user role:', error);
    throw new Error(error.message || 'An unknown error occurred.');
  }
}

export async function fetchPermissionsApi(page:any) {
  try {
      const response = await authService.apiCall(`${BASE_URL}/groups/permissions?page=${page}`, {
          method: 'GET',
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
          if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
              throw new Error("Forbidden: You do not have permission to access this resource.");
          }
          throw new Error("Failed to fetch");
      }
      return data;
  } catch (error: any) {
      throw new Error(error?.message || error || 'Failed to fetch data');
  }
}

// Fetch all groups (id, name, permissions)
export async function fetchAllGroupApi(): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/groups/getAll`, {
      method: 'GET'
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (response.status === 401 || response.status === 403 || data.message === 'Forbidden access') {
        throw new Error('Forbidden: You do not have permission to access this resource.');
      }
      throw new Error('Failed to fetch');
    }
    return data;
  } catch (error: any) {
    throw new Error(error);
  }
}

// Update a group's permissions by id
export async function updatePermissionApi(id: string, body: any): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (response.status === 401 || response.status === 403 || data.message === 'Forbidden access') {
        throw new Error('Forbidden: You do not have permission to access this resource.');
      }
      throw new Error('Failed to update');
    }
    return data;
  } catch (error: any) {
    throw new Error(error);
  }
}

export async function registerUsersBulk(users: any[]) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/users/register-users`, {
      method: 'POST',
      body: JSON.stringify({ users }),
    });

    const result = await response.json();
    return result;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to register users');
  }
}

export async function submitQuestionsAndAnswers(questionAnswers: any[]) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/publicSurvey/process`, {
      method: 'POST',
      body: JSON.stringify({ questionAnswers }),
    });

    const result = await response.json();
    return result;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to submit questions and answers');
  }
}

export async function getUserInfo(): Promise<any> {
  try {
    // First try to get user info from token (client-side, same as new_demo_frontend)
    const { getV2Token } = await import('../utils/v2AuthUtils');
    const { validateToken } = await import('../utils/tokenValidator');
    
    const tokenFromCookieOrV2 = getV2Token() || getToken();
    if (tokenFromCookieOrV2) {
      const userData = validateToken(tokenFromCookieOrV2);
      if (userData) {
        // Try to get full user data from backend using user ID
        try {
          const token = getToken();
          const csrfToken = await getCsrfToken();
          const headers = {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "X-CSRF-TOKEN": csrfToken,
          };

          const response = await fetch(`${BASE_URL}/users/${userData.id}`, {
            method: 'GET',
            credentials: "include",
            headers,
          });
console.log("///////////",response)
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const text = await response.text();
              if (text && text.trim() !== '') {
                const data = JSON.parse(text);
                return data.data || data;
              }
            }
          }
        } catch (backendError) {
          console.warn('Failed to fetch user from backend, using token data:', backendError);
        }
        
        // Fallback to token data if backend call fails
        return {
          id: userData.id,
          username: userData.username,
          name: userData.name,
          email: userData.email,
          title: userData.title,
          groupId: userData.group,
          group: { name: userData.groupName, permissions: userData.permissions },
          lastLogin: null,
        };
      }
    }
    
    // If no token, try backend endpoint (for backward compatibility)
    const token = getToken();
    const csrfToken = await getCsrfToken();
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-CSRF-TOKEN": csrfToken,
    };

    const response = await fetch(`${BASE_URL}/users/me`, {
      method: 'GET',
      credentials: "include",
      headers,
    });

    // Check if response is ok
    if (!response.ok) {
      // If 401 or 403, user is not authenticated
      if (response.status === 401 || response.status === 403) {
        return null;
      }
      return null;
    }

    // Check if response has content
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return null;
    }

    const text = await response.text();
    if (!text || text.trim() === '') {
      return null;
    }

    const data = JSON.parse(text);
    return data.data || data;
  } catch (error: any) {
    console.error('getUserInfo error:', error);
    // Return null instead of throwing to prevent breaking the app
    return null;
  }
}

export async function getInfouserById(id: string) {
  try {
    const token = getToken();
    const csrfToken = await getCsrfToken();
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-CSRF-TOKEN": csrfToken,
    };

    const response = await fetch(`${BASE_URL}/users/${id}`, {
      method: 'GET',
      credentials: "include",
      headers,
    });

    const data = await response.json();

    if (!response.ok || !data) {
      if (response.status === 401 || response.status === 403 || data?.message === "Forbidden access") {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error(data?.message || 'Failed to get user info');
    }

    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to get user info');
  }
}

export async function getAllOrganization() {
  try {
    const response = await authService.apiCall(`${BASE_URL}/tenant/tenant-ids`, {
      method: 'GET',
    });

    const data = await response.json();
    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to get organizations');
  }
}

export const submitRegisterUser = async (data: any) => {
  try {
    const response = await authService.apiCall(`${BASE_URL}/users/register`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    const result = await response.json();
    return result;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to register user');
  }
};

// Logout API function (same as landing and v2-frontend)
export async function logoutApi(): Promise<any> {
  try {
    const token = getV2Token();
    if (!token) {
      throw new Error("Forbidden: You do not have permission to access this resource.");
    }
    
    const csrfToken = await getCsrfToken();
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-CSRF-TOKEN": csrfToken,
    };
    
    const response = await fetch(`${BASE_URL}/auth/force-logout`, {
      credentials: "include",
      method: 'POST',
      headers,
    });

    const data = await response.json();
    if (data.statusCode === 401 || data.statusCode === 403) {
      throw new Error("Forbidden: You do not have permission to access this resource.");
    }
    return data;
  } catch (error) {
    console.error('Error in logoutApi:', error);
    // Don't show toast here, let the caller handle it
    throw error;
  }
}